import { formatNaturalDate } from "@memorang/helpers/date";
import {
	type ProficiencyBandFill,
	type SkillTestScores,
	calculateSkillTestScores,
} from "@memorang/helpers/language-learning";
import type { MixedItemDist, MixedItemTag } from "@memorang/types";
import { toConnectedLevels } from "@memorang/ui/components/ConnectedLevels/helpers";
import type { LanguageLearningProficiencyContent } from "@memorang/ui/components/LanguageLearningProgressApplet";
import type { ProficiencyScoreDonut } from "@memorang/ui/components/SkillProgressCards/ProficiencyLevelsCard";
import type { LevelIconName } from "@memorang/ui/icon";
import type { TFunction } from "i18next";

const buildProficiencyScoreDonuts = (
	testScores: SkillTestScores,
	maxScore: number,
	t: TFunction,
): ProficiencyScoreDonut[] => {
	const latest = {
		value: testScores.latest.score,
		maxScore,
		label: t("progress.languageLearning.latestLabel"),
		subLabel: formatNaturalDate(testScores.latest.dateCompleted),
	};

	// One test can be both the best and the latest. Showing it twice would invent
	// a comparison that does not exist, and calling a lone result a personal best
	// overstates it — so it reads simply as the latest score.
	if (testScores.best.examId === testScores.latest.examId) {
		return [latest];
	}

	return [
		{
			value: testScores.best.score,
			maxScore,
			label: t("progress.languageLearning.personalBestLabel"),
			subLabel: formatNaturalDate(testScores.best.dateCompleted),
		},
		latest,
	];
};

type BuildProficiencyContentArgs = {
	skill: MixedItemTag;
	fills: ProficiencyBandFill[];
	level?: string;
	levelIcons: LevelIconName[];
	showProficiencyLevels: boolean;
	proficiencyTargetScore: number;
	exams?: MixedItemDist["exams"];
	canStartTest: boolean;
	noScoresGraphicUrl?: string;
	t: TFunction;
};

export const resolveProficiencyVariant = ({
	showProficiencyLevels,
	proficiencyTargetScore,
}: Pick<
	BuildProficiencyContentArgs,
	"showProficiencyLevels" | "proficiencyTargetScore"
>): "levels" | "scores" =>
	showProficiencyLevels || proficiencyTargetScore <= 0 ? "levels" : "scores";

/**
 * Picks the card's single content mode. Deciding here — the one place that knows
 * both the app's display mode and whether any test has been scored — is what
 * keeps "no tests yet" from being rendered as a real score of zero.
 */
export const buildProficiencyContent = ({
	skill,
	fills,
	level,
	levelIcons,
	showProficiencyLevels,
	proficiencyTargetScore,
	exams,
	canStartTest,
	noScoresGraphicUrl,
	t,
}: BuildProficiencyContentArgs): {
	content: LanguageLearningProficiencyContent;
	cardSubtitle?: string;
} => {
	// A non-positive target score cannot produce a meaningful estimate, so fall
	// back to levels rather than promising a score the app cannot compute. That
	// is a misconfiguration rather than a levels app, so it gets no tooltip.
	if (
		resolveProficiencyVariant({
			showProficiencyLevels,
			proficiencyTargetScore,
		}) === "levels"
	) {
		return {
			content: {
				kind: "levels",
				levels: toConnectedLevels(fills, levelIcons),
				tooltip:
					showProficiencyLevels && level
						? t("progress.languageLearning.proficiencyCardTooltip", {
								skill: skill.title,
								level,
							})
						: undefined,
			},
		};
	}

	const testScores = calculateSkillTestScores(
		exams,
		skill,
		proficiencyTargetScore,
	);

	// Keyed on the absence of scores, never on a score of 0 — an earned zero is
	// a real result and still belongs in the donuts.
	if (!testScores) {
		return {
			content: {
				kind: "noScores",
				empty: {
					title: t("progress.languageLearning.noScoresTitle", {
						skill: skill.title,
					}),
					subtitle: t("progress.languageLearning.noScoresSubtitle"),
					graphicUrl: noScoresGraphicUrl,
					...(canStartTest
						? { cta: t("progress.languageLearning.noScoresCta") }
						: {}),
				},
			},
			cardSubtitle: t("progress.languageLearning.noScoresCardSubtitle"),
		};
	}

	return {
		content: {
			kind: "scores",
			scores: buildProficiencyScoreDonuts(
				testScores,
				proficiencyTargetScore,
				t,
			),
		},
		cardSubtitle: t("progress.languageLearning.testsTakenSubtitle", {
			count: testScores.testsTaken,
		}),
	};
};
